import { Component, ChangeDetectionStrategy, DestroyRef, ElementRef, computed, inject, signal } from '@angular/core';
import { ToastService } from '../../core/services/toast.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, RouterLink } from '@angular/router';
import { switchMap, EMPTY, fromEvent } from 'rxjs';
import { filter } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { SessionContextService } from '../../core/services/session-context.service';
import { CaseContextService } from '../../core/services/case-context.service';
import { CaseApiService } from '../../core/services/case-api.service';
import { CaseModalService } from '../../features/broker/cases/case-modal.service';
import { INTENT_LABELS } from '../../core/services/case.model';
import { DevUserPickerComponent } from '../../features/admin/dev-user-picker.component';
import { DevUserService } from '../../features/admin/dev-user.service';
import { AuthService } from '../../core/services/auth.service';

@Component({
  selector: 'app-topbar',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, RouterLink, DevUserPickerComponent],
  templateUrl: './topbar.component.html',
  styleUrl: './topbar.component.scss',
})
export class TopbarComponent {
  readonly toastSvc = inject(ToastService);
  protected session = inject(SessionContextService);
  private router = inject(Router);
  private host = inject(ElementRef);
  private destroyRef = inject(DestroyRef);

  // Case-pill wiring — moved here from app-case-bar so the active case
  // sits in the topbar's empty middle and reclaims the case-bar row.
  readonly caseCtx = inject(CaseContextService);
  private caseApi = inject(CaseApiService);
  private caseModal = inject(CaseModalService);
  private readonly intentLabels = INTENT_LABELS;

  readonly menuOpen = signal(false);

  /** Dev impersonation. Picker UI was previously rendered in the sidebar's
   *  top section — moved into the topbar's avatar dropdown so non-dev
   *  surfaces don't waste real estate on it. The orange dot on the avatar
   *  is the always-visible "you are impersonating" cue. */
  readonly devUser = inject(DevUserService);
  private auth = inject(AuthService);
  readonly devPickerOpen = signal(false);
  readonly isImpersonating = computed(() => this.devUser.currentUser() !== null);

  toggleDevPicker(): void {
    this.devPickerOpen.update(v => !v);
    this.closeMenu();
  }
  closeDevPicker(): void { this.devPickerOpen.set(false); }

  /** Compact summary line under the case name in the topbar pill —
   *  age · state · premium. Mirrors the old case-bar pill's summary
   *  but trims the term/intent bits so the pill stays narrow. */
  readonly caseSummary = computed(() => {
    const c = this.caseCtx.activeCase();
    if (!c) return '';
    const bits: string[] = [];
    if (c.age != null) bits.push(`${c.age}`);
    if (c.state) bits.push(c.state);
    if (c.premium != null) bits.push(`$${(c.premium / 1000).toFixed(0)}K`);
    return bits.join(' · ');
  });

  togglePicker(): void { this.caseCtx.togglePicker(); }
  clearCase(): void { this.caseCtx.clear(); }
  editCase(): void {
    const active = this.caseCtx.activeCase();
    if (!active) return;
    this.caseModal.open(active).pipe(
      switchMap(input => input ? this.caseApi.update(active.id, input) : EMPTY),
      switchMap(() => this.caseApi.list()),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(list => {
      const fresh = list.find(c => c.id === active.id);
      if (fresh) this.caseCtx.setActive(fresh);
    });
  }

  /** First initial of first name + first initial of last name. Falls back
   *  to "U" when no session is loaded. */
  readonly avatarInitials = computed(() => {
    const u = this.session.context()?.user;
    if (!u) return 'U';
    const first = (u as any).first_name ?? (u as any).firstName ?? '';
    const last  = (u as any).last_name ?? (u as any).lastName ?? '';
    const a = (first?.[0] ?? '').toUpperCase();
    const b = (last?.[0] ?? '').toUpperCase();
    return (a + b) || 'U';
  });

  readonly displayName = computed(() => {
    const u = this.session.context()?.user;
    if (!u) return 'Sign in';
    const first = (u as any).first_name ?? (u as any).firstName ?? '';
    const last  = (u as any).last_name ?? (u as any).lastName ?? '';
    return `${first} ${last}`.trim() || (u as any).email || 'User';
  });

  readonly companyName = computed(() => this.session.context()?.companyName ?? '');

  /** Where the gear/Admin button lands — /admin lets adminLandingRedirect
   *  pick the right destination per role; null hides the button.
   *
   *  There used to be a `/commissions` fallback here for users who failed
   *  canAccessAdmin(). It was already a dead end — /commissions redirects to
   *  /admin/commissions, which sits behind beaconAdminGuard (the very check that
   *  just failed), so anyone reaching it was bounced straight to /dashboard.
   *  Removed rather than repointed: no non-admin commissions shell exists today. */
  readonly settingsRoute = computed<string | null>(() =>
    this.session.canAccessAdmin() ? '/admin' : null
  );

  /** The Admin pill is a second entry point to /admin, so it follows the same
   *  rule as the nav item and the route guard: admin === 1 && !suppress_admin.
   *  Previously it showed whenever no session had loaded yet. */
  readonly showAdminPill = computed<boolean>(() => this.session.canAccessAdmin());

  toggleMenu(): void {
    this.menuOpen.update(v => !v);
  }

  closeMenu(): void {
    this.menuOpen.set(false);
  }

  navigate(path: string): void {
    this.closeMenu();
    this.router.navigateByUrl(path);
  }

  signOut(): void {
    this.closeMenu();
    // Explicit sign-out is the one path that should also discard the selected
    // user — automatic stale-credential cleanup leaves it in place.
    this.auth.logout({ clearDevUser: true });
  }

  onToggleNewShell(): void {
    this.closeMenu();
    this.session.setEnableHorizontalNav(!this.session.enableHorizontalNav());
  }

  constructor() {
    fromEvent<MouseEvent>(document, 'click').pipe(
      filter(ev => !this.host.nativeElement.contains(ev.target as Node)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      if (this.menuOpen()) this.closeMenu();
      if (this.devPickerOpen()) this.closeDevPicker();
    });
  }
}
